import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, EyeOff, Lock, LogOut, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BRANDS } from "@/data/products";
import BulkRequestsTab from "@/components/BulkRequestsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gb-admin-token";

const inputCls =
  "rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-pink-400 focus:outline-none w-full";

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token, data.email);
      toast.success("Welcome back!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111] p-8 space-y-5" data-testid="admin-login-form">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-amber-300" />
          </span>
          <div>
            <h1 className="font-extrabold text-xl text-white">Admin Sign In</h1>
            <p className="text-sm text-zinc-500">Glow &amp; Bling dashboard</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm text-zinc-400" htmlFor="login-email">Email</label>
          <input id="login-email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm text-zinc-400" htmlFor="login-password">Password</label>
          <div className="relative">
            <input id="login-password" data-testid="login-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} !pr-11`} />
            <button type="button" data-testid="login-toggle-password" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-lg bg-white text-black font-bold text-sm py-3 uppercase tracking-widest hover:bg-zinc-200 disabled:opacity-50">
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const SalesTab = ({ authH }) => {
  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sales?range=${range}`, authH()).then((r) => setData(r.data)).catch(() => toast.error("Could not load sales"));
  }, [range]);

  const brandMeta = [
    ["lemonade", "🍋 Lani Lemonade", "border-pink-500/40 bg-pink-500/5"],
    ["glow", "✨ Bourne To Glow", "border-amber-400/40 bg-amber-400/5"],
    ["bling", "💎 Bourne For Bling", "border-purple-500/40 bg-purple-500/5"],
  ];

  return (
    <div data-testid="sales-tab" className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {[["today", "Today"], ["7d", "Last 7 Days"], ["30d", "Last 30 Days"], ["all", "All Time"]].map(([k, l]) => (
          <button key={k} data-testid={`sales-range-${k}`} onClick={() => setRange(k)}
            className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${range === k ? "bg-white text-black" : "border border-white/15 text-zinc-400 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-[#111] p-6">
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Total Orders</p>
          <p className="mt-2 text-3xl font-extrabold text-white" data-testid="sales-total-orders">{data?.total_orders ?? "—"}</p>
        </div>
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-6">
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-300/80">Gross Revenue</p>
          <p className="mt-2 text-3xl font-extrabold text-white" data-testid="sales-gross">${(data?.gross ?? 0).toFixed(2)}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {brandMeta.map(([key, label, cls]) => {
          const b = data?.brands?.[key];
          return (
            <div key={key} className={`rounded-2xl border p-6 ${cls}`} data-testid={`sales-brand-${key}`}>
              <p className="font-bold text-white">{label}</p>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-zinc-400"><dt>Revenue</dt><dd className="text-white font-semibold">${(b?.revenue ?? 0).toFixed(2)}</dd></div>
                <div className="flex justify-between text-zinc-400"><dt>Orders</dt><dd className="text-white">{b?.orders ?? 0}</dd></div>
                <div className="flex justify-between text-zinc-400"><dt>Units sold</dt><dd className="text-white">{b?.units ?? 0}</dd></div>
                <div className="flex justify-between text-zinc-400 pt-2 border-t border-white/10"><dt>Avg order</dt><dd className="text-white font-semibold">${(b?.avg ?? 0).toFixed(2)}</dd></div>
              </dl>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-600">Includes orders with status: Paid, Ready, Completed. Delivery fees excluded from brand revenue.</p>
    </div>
  );
};

const emptyNew = { brand: "bling", sku: "", name: "", cat: "", price: "", image: "", description: "" };

const ProductsTab = ({ authH, products, refresh }) => {
  const [edits, setEdits] = useState({});
  const [adding, setAdding] = useState(false);
  const [np, setNp] = useState(emptyNew);

  useEffect(() => {
    const map = {};
    products.forEach((p) => { map[p.id] = { ...p, price: p.price ?? "" }; });
    setEdits(map);
  }, [products]);

  const setField = (id, field, value) =>
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const save = async (id) => {
    const row = edits[id];
    try {
      await axios.put(`${API}/products/${id}`, { ...row, price: row.price === "" ? null : parseFloat(row.price) }, authH());
      toast.success("Product saved");
      refresh();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, authH());
      toast.success("Product deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const create = async () => {
    if (!np.name.trim()) return toast.error("Name is required");
    try {
      await axios.post(`${API}/products`, { ...np, price: np.price === "" ? null : parseFloat(np.price), in_stock: true }, authH());
      toast.success("Product added");
      setAdding(false);
      setNp(emptyNew);
      refresh();
    } catch {
      toast.error("Add failed");
    }
  };

  return (
    <div data-testid="products-tab" className="space-y-4">
      <button data-testid="add-product-btn" onClick={() => setAdding(!adding)}
        className="inline-flex items-center gap-2 rounded-lg bg-amber-400 text-black text-sm font-bold px-5 py-2.5 hover:bg-amber-300">
        <Plus className="w-4 h-4" /> Add Product
      </button>

      {adding && (
        <div className="rounded-xl border border-amber-400/40 bg-[#111] p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="add-product-form">
          <select value={np.brand} onChange={(e) => setNp({ ...np, brand: e.target.value })} className={inputCls} data-testid="new-product-brand">
            <option value="bling">Bling</option><option value="glow">Glow</option><option value="lemonade">Lemonade</option>
          </select>
          <input placeholder="SKU" value={np.sku} onChange={(e) => setNp({ ...np, sku: e.target.value })} className={inputCls} data-testid="new-product-sku" />
          <input placeholder="Name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className={inputCls} data-testid="new-product-name" />
          <input placeholder="Category" value={np.cat} onChange={(e) => setNp({ ...np, cat: e.target.value })} className={inputCls} data-testid="new-product-cat" />
          <input placeholder="Price (e.g. 8.00, blank = TBD)" type="number" step="0.01" value={np.price} onChange={(e) => setNp({ ...np, price: e.target.value })} className={inputCls} data-testid="new-product-price" />
          <input placeholder="Image URL (/brand/…)" value={np.image} onChange={(e) => setNp({ ...np, image: e.target.value })} className={inputCls} data-testid="new-product-image" />
          <input placeholder="Description" value={np.description} onChange={(e) => setNp({ ...np, description: e.target.value })} className={`${inputCls} sm:col-span-2`} data-testid="new-product-description" />
          <button onClick={create} className="rounded-lg bg-white text-black text-sm font-bold px-5 py-2" data-testid="new-product-save">Save Product</button>
        </div>
      )}

      <div className="space-y-3">
        {products.map((p) => {
          const row = edits[p.id] || p;
          return (
            <div key={p.id} data-testid={`product-row-${p.id}`} className="rounded-xl border border-white/10 bg-[#111] p-3 flex flex-wrap items-center gap-3">
              <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-black/40" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 w-16">{p.brand}</span>
              <input value={row.sku || ""} onChange={(e) => setField(p.id, "sku", e.target.value)} className={`${inputCls} !w-28`} data-testid={`edit-sku-${p.id}`} />
              <input value={row.name || ""} onChange={(e) => setField(p.id, "name", e.target.value)} className={`${inputCls} flex-1 min-w-[160px]`} data-testid={`edit-name-${p.id}`} />
              <input value={row.cat || ""} onChange={(e) => setField(p.id, "cat", e.target.value)} className={`${inputCls} !w-32`} placeholder="Category" />
              <input value={row.price} type="number" step="0.01" onChange={(e) => setField(p.id, "price", e.target.value)} className={`${inputCls} !w-24`} placeholder="TBD" data-testid={`edit-price-${p.id}`} />
              <button onClick={() => save(p.id)} className="rounded-lg border border-white/15 p-2.5 text-zinc-300 hover:border-emerald-400 hover:text-emerald-300" data-testid={`save-${p.id}`} aria-label="Save product">
                <Save className="w-4 h-4" />
              </button>
              <button onClick={() => del(p.id)} className="rounded-lg bg-red-500/90 p-2.5 text-white hover:bg-red-500" data-testid={`delete-${p.id}`} aria-label="Delete product">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StockTab = ({ authH, products, refresh }) => {
  const lani = products.filter((p) => p.brand === "lemonade" && p.cat !== "Combos");
  const groups = ["Classic", "Fall Flavors", "Flavored", "Specialty"];

  const toggle = async (p) => {
    try {
      await axios.put(`${API}/products/${p.id}`, { ...p, in_stock: !p.in_stock }, authH());
      refresh();
    } catch {
      toast.error("Stock update failed");
    }
  };

  return (
    <div data-testid="stock-tab" className="space-y-8">
      <div className="rounded-2xl border border-pink-500/30 bg-pink-500/5 p-5">
        <h3 className="font-bold text-white">🍋 Lani Lemonade Stock Control</h3>
        <p className="text-sm text-zinc-400 mt-1">Toggle any flavor to instantly mark it Sold Out on the site. Sold-out flavors are disabled in flavor pickers (combos) and product cards.</p>
      </div>
      {groups.map((g) => {
        const items = lani.filter((p) => p.cat === g);
        if (!items.length) return null;
        return (
          <div key={g}>
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">{g} ({items.length})</p>
            <div className="grid md:grid-cols-2 gap-3">
              {items.map((p) => (
                <div key={p.id} className="rounded-xl border border-white/10 bg-[#111] p-3 flex items-center gap-3" data-testid={`stock-row-${p.id}`}>
                  <img src={p.image} alt="" className={`w-12 h-12 rounded-lg object-cover ${p.in_stock ? "" : "grayscale opacity-50"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                    <p className="text-xs text-zinc-500">{p.sku}</p>
                  </div>
                  <button
                    onClick={() => toggle(p)}
                    data-testid={`stock-toggle-${p.id}`}
                    className={`relative w-11 h-6 rounded-full transition-colors ${p.in_stock ? "bg-emerald-400" : "bg-zinc-700"}`}
                    aria-label={`Toggle stock for ${p.name}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${p.in_stock ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.15em] w-16 text-right ${p.in_stock ? "text-emerald-300" : "text-red-400"}`}>
                    {p.in_stock ? "In Stock" : "Sold Out"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ORDER_STATUSES = ["reserved", "paid", "ready", "completed", "cancelled"];

const OrdersTab = ({ authH }) => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showClosed, setShowClosed] = useState(false);

  const load = () => axios.get(`${API}/orders`, authH()).then((r) => setOrders(r.data)).catch(() => toast.error("Could not load orders"));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/orders/${id}`, { status }, authH());
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    await axios.delete(`${API}/orders/${id}`, authH()).then(load).catch(() => toast.error("Delete failed"));
  };

  const hasBrand = (o, b) => o.items.some((i) => i.brand === b);
  const filtered = filter === "all" ? orders : orders.filter((o) => hasBrand(o, filter));
  const active = filtered.filter((o) => ["reserved", "paid", "ready"].includes(o.status));
  const closed = filtered.filter((o) => ["completed", "cancelled"].includes(o.status));

  const filters = [
    ["all", "All Businesses", orders.length],
    ["lemonade", "🍋 Lani Lemonade", orders.filter((o) => hasBrand(o, "lemonade")).length],
    ["glow", "✨ Bourne To Glow", orders.filter((o) => hasBrand(o, "glow")).length],
    ["bling", "💎 Bourne For Bling", orders.filter((o) => hasBrand(o, "bling")).length],
  ];

  const OrderCard = ({ o }) => (
    <div className="rounded-xl border border-white/10 bg-[#111] p-4 space-y-3" data-testid={`order-card-${o.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-white">#{o.id.slice(0, 8)} · {o.customer_name}</p>
          <p className="text-xs text-zinc-500">
            {o.source === "pos" ? `POS · ${o.payment_method}${o.card_entry ? ` (${{ reader: "Tap / Reader", keyed: "Keyed In", square_tap: "Square Tap" }[o.card_entry] || o.card_entry})` : ""}` : `Online · ${o.fulfillment}`}{o.phone ? ` · ${o.phone}` : ""} · {new Date(o.created_at).toLocaleString()}
          </p>
          {o.delivery_address && <p className="text-xs text-zinc-400">📍 {o.delivery_address}</p>}
        </div>
        <div className="flex items-center gap-2">
          {o.items.some((i) => i.brand === "lemonade") && (
            <a href={`/print/${o.id}`} data-testid={`order-print-${o.id}`} className="rounded-lg border border-white/15 px-2.5 py-2 text-zinc-300 hover:border-white/50 text-sm" aria-label="Print cup stickers">
              🖨️
            </a>
          )}
          <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className={`${inputCls} !w-32`} data-testid={`order-status-${o.id}`}>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => del(o.id)} className="rounded-lg bg-red-500/90 p-2 text-white hover:bg-red-500" data-testid={`order-delete-${o.id}`} aria-label="Delete order">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <ul className="text-sm text-zinc-300 space-y-1">
        {o.items.map((i, idx) => (
          <li key={idx} className="flex justify-between gap-4">
            <span>{i.qty} × {i.name}{i.variant ? <span className="text-zinc-500"> ({i.variant})</span> : null}</span>
            <span className="text-zinc-400">${(i.price * i.qty).toFixed(2)}</span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between text-sm font-bold text-white pt-2 border-t border-white/10">
        <span>Total{o.tip > 0 ? ` (incl. $${o.tip.toFixed(2)} tip)` : ""}</span><span className="text-amber-300">${o.total.toFixed(2)}</span>
      </div>
    </div>
  );

  return (
    <div data-testid="orders-tab" className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {filters.map(([k, l, n]) => (
          <button key={k} data-testid={`filter-brand-${k}`} onClick={() => setFilter(k)}
            className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${filter === k ? "bg-white text-black" : "border border-white/15 text-zinc-400 hover:text-white"}`}>
            {l} <span className="ml-1 opacity-70">{n}</span>
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-5 space-y-4">
        <p className="font-bold text-white">🔔 Active Orders ({active.length}) <span className="block text-xs font-normal text-zinc-500">Reserved, Paid or Ready — needs action</span></p>
        {active.length === 0 ? <p className="text-sm italic text-zinc-500">No orders in this bucket.</p> : active.map((o) => <OrderCard key={o.id} o={o} />)}
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#111] p-5">
        <button className="w-full text-left font-bold text-zinc-300" onClick={() => setShowClosed(!showClosed)} data-testid="toggle-closed-orders">
          🗃 Closed ({closed.length}) <span className="block text-xs font-normal text-zinc-500">Completed or cancelled</span> {showClosed ? "▲" : "▼"}
        </button>
        {showClosed && (
          <div className="mt-4 space-y-4">
            {closed.length === 0 ? <p className="text-sm italic text-zinc-500">No orders in this bucket.</p> : closed.map((o) => <OrderCard key={o.id} o={o} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState(null);
  const [tab, setTab] = useState("sales");
  const [catalog, setCatalog] = useState([]);
  const [checking, setChecking] = useState(!!token);

  const authH = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const loadProducts = () =>
    axios.get(`${API}/products`, authH()).then((r) => setCatalog(r.data)).catch(() => {});

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, authH())
      .then((r) => { setEmail(r.data.email); loadProducts(); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); })
      .finally(() => setChecking(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEmail(null);
  };

  if (checking) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Loading…</div>;
  if (!token || !email) return <LoginCard onLogin={(t, e) => { setToken(t); setEmail(e); }} />;

  const tabs = [
    ["sales", "📊 Sales"],
    ["products", `Products (${catalog.length})`],
    ["stock", "🍋 Lani Stock"],
    ["orders", "Orders"],
    ["bulk", "📋 Bulk Requests"],
  ];

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-white">Admin</h1>
          <p className="text-sm text-zinc-500 mt-1">Signed in as {email}</p>
        </div>
        <div className="flex items-center gap-4">
          <a href="/pos" data-testid="admin-pos-link" className="inline-flex items-center gap-2 rounded-lg bg-pink-500 text-white text-sm font-bold px-4 py-2 hover:bg-pink-400 transition-colors">
            Open POS
          </a>
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>
      <div className="mt-6 flex gap-6 border-b border-white/10 overflow-x-auto">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`pb-3 text-sm whitespace-nowrap transition-colors ${tab === k ? "text-white font-bold border-b-2 border-amber-400" : "text-zinc-500 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "sales" && <SalesTab authH={authH} />}
        {tab === "products" && <ProductsTab authH={authH} products={catalog} refresh={loadProducts} />}
        {tab === "stock" && <StockTab authH={authH} products={catalog} refresh={loadProducts} />}
        {tab === "orders" && <OrdersTab authH={authH} />}
        {tab === "bulk" && <BulkRequestsTab authH={authH} />}
      </div>
    </main>
  );
}
